import type { ReactNode } from "react";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./ChartCard.module.css";

// 통계 카드 한 장의 셸이다. 제목·부제·우측 액션 공간을 두고, 본문은 차트가 채운다.
// 카드가 자기 padding 을 소유한다. 그래서 페이지 레이아웃은 grid 간격만 정한다(`docs/rules.md` §14).
// 본문을 대신하는 대기·실패·빈 상태의 형태도 카드가 소유한다. 호출부는 문구와 재조회만 넘긴다.
export function ChartCard({
  title,
  subtitle,
  action,
  empty,
  loading,
  error,
  inactive,
  children,
}: {
  title: string;
  /** 숫자를 읽는 데 필요한 정보다(예: 모집단, 접힌 개수). */
  subtitle?: string;
  /** 제목 우측 슬롯이다. 컨트롤(예: 버킷 전환)이나 상태 표시를 둔다. */
  action?: ReactNode;
  /** 값이 있으면 본문 대신 이 문구를 그린다. 조회는 성공했고 표본이 없는 축에 쓴다. */
  empty?: string;
  /** 값이 있으면 본문 대신 대기를 그린다. 대기 문구를 `empty` 로 넘기지 않는다. */
  loading?: string;
  /** 값이 있으면 본문 대신 조회 실패를 그린다. `onRetry` 를 주면 `다시 시도` 를 둔다. */
  error?: { message: string; onRetry?: () => void };
  /**
   * 현재 값이 아닌 카드다. 예: 끝난 pod 처럼 창 안 이력에만 남은 항목.
   *
   * 값을 지우지 않고 흐리게만 표시한다. 이 화면은 이력 자체에 답한다(예: 재시작 직전에
   * 메모리가 어땠는가). 그래서 숨기면 안 된다. 하지만 실행 중인 항목과 같은 강도로 표시하면
   * 사용자가 목록 길이를 현재 규모로 읽는다.
   */
  inactive?: boolean;
  children?: ReactNode;
}) {
  // 카드가 이미 sunken 면이다. 그래서 안내는 면 없이 본문 자리의 가운데에 둔다.
  // 실패가 대기와 빈 상태보다 먼저다. 받지 못한 것을 "없음"으로 알리지 않는다.
  const state =
    error !== undefined ? (
      <EmptyState
        size="sm"
        mode="plain"
        tone="error"
        align="center"
        action={
          error.onRetry === undefined ? undefined : { label: "다시 시도", onClick: error.onRetry }
        }
      >
        {error.message}
      </EmptyState>
    ) : loading !== undefined ? (
      <LoadingState size="sm" mode="plain">
        {loading}
      </LoadingState>
    ) : empty !== undefined ? (
      <EmptyState size="sm" mode="plain" align="center">
        {empty}
      </EmptyState>
    ) : null;
  return (
    <section className={styles.card} data-inactive={inactive === true ? "true" : undefined}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <span className={styles.title}>{title}</span>
          {subtitle !== undefined && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        {action}
      </div>
      {state === null ? (
        <div className={styles.body}>{children}</div>
      ) : (
        <div className={styles.state}>{state}</div>
      )}
    </section>
  );
}
